import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Zap, Users, MapPin, MessageCircle } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const goApp = () => navigate(user ? "/home" : "/login");

  return (
    <div className="min-h-screen bg-[#09090B] text-foreground overflow-x-hidden">
      {/* Glow background */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[600px] overflow-hidden">
        <div className="absolute left-1/2 -translate-x-1/2 -top-40 h-[500px] w-[500px] rounded-full bg-primary/20 blur-[120px]" />
      </div>

      {/* Nav */}
      <header className="relative z-10 mx-auto max-w-6xl px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="brand">
          <div className="h-8 w-8 rounded-xl bg-primary glow-cyan flex items-center justify-center">
            <span className="font-heading font-black text-black">J</span>
          </div>
          <span className="font-heading font-black text-lg tracking-tight">JoinPlay</span>
        </div>
        <button
          data-testid="cta-login-top"
          onClick={goApp}
          className="text-sm font-bold text-foreground/80 hover:text-foreground"
        >
          {user ? "Buka App" : "Masuk"}
        </button>
      </header>

      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-3xl px-6 pt-10 pb-20 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary border border-border text-xs font-bold mb-6">
          <span className="h-2 w-2 rounded-full bg-primary pulse-cyan" />
          MVP · Indonesia
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
          Cari Teman Main Olahraga
          <br />
          <span className="text-primary text-glow-cyan">Tanpa Chaos Grup WhatsApp</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
          Join padel, futsal, badminton, dan olahraga lainnya di sekitar lo dalam hitungan detik. Slot real-time, host yang reliable.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            data-testid="cta-find-match"
            onClick={goApp}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold glow-cyan-strong active:scale-95 transition-transform"
          >
            Cari Match <ArrowRight className="h-4 w-4" />
          </button>
          <button
            data-testid="cta-create-match"
            onClick={goApp}
            className="w-full sm:w-auto px-6 py-3.5 rounded-full bg-secondary border border-border text-foreground font-bold hover:border-primary/40 active:scale-95 transition-all"
          >
            Buat Match
          </button>
        </div>

        {/* Mock cards preview */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { sport: "Padel", venue: "GOR Senayan", slots: "3/4", color: "#00E5FF", icon: "🎾", time: "Malam ini · 19:00" },
            { sport: "Futsal", venue: "Lapangan Sudirman", slots: "8/10", color: "#FF6B35", icon: "⚽", time: "Besok · 20:00" },
            { sport: "Badminton", venue: "GOR Kuningan", slots: "2/4", color: "#A78BFA", icon: "🏸", time: "Sabtu · 17:00" },
          ].map((c) => (
            <div key={c.sport} className="bg-card border border-border rounded-2xl p-5 text-left relative overflow-hidden">
              <div aria-hidden className="absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl opacity-20" style={{ background: c.color }} />
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center text-2xl" style={{ background: c.color + "22", border: `1px solid ${c.color}44` }}>{c.icon}</div>
                <div>
                  <div className="font-heading font-bold">{c.sport}</div>
                  <div className="text-xs text-muted-foreground">{c.venue}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">{c.time}</div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-1 font-bold text-primary"><Users className="h-3.5 w-3.5" /> {c.slots}</div>
                <span className="text-primary font-bold">Join →</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 mx-auto max-w-5xl px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl font-black text-center mb-12 tracking-tight">Kenapa JoinPlay?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Zap, title: "Join Sekejap", body: "Tap, join, main. Tanpa drama WA." },
            { icon: MapPin, title: "Dekat Lo", body: "Cari match di kota lo, real-time." },
            { icon: Users, title: "Anti Cancel", body: "Reliability score buat host & player." },
            { icon: MessageCircle, title: "Chat Temporer", body: "Room auto-archived setelah main." },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-card border border-border rounded-2xl p-5">
                <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-3 font-heading font-bold">{f.title}</div>
                <div className="mt-1 text-sm text-muted-foreground">{f.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 mx-auto max-w-3xl px-6 py-20 text-center">
        <div className="bg-card border border-border rounded-3xl p-8 sm:p-12 relative overflow-hidden">
          <div aria-hidden className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
          <h3 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Gas, mulai main hari ini.</h3>
          <p className="mt-3 text-muted-foreground">Login pakai Google. 30 detik kelar.</p>
          <button
            data-testid="cta-final"
            onClick={goApp}
            className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary text-primary-foreground font-bold glow-cyan-strong active:scale-95 transition-transform"
          >
            {user ? "Buka App" : "Masuk Sekarang"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="relative z-10 py-8 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} JoinPlay · Made for Indonesian athletes
      </footer>
    </div>
  );
}
